import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Save, ArrowLeft, Plus, Trash2, ChevronDown, ChevronUp, Building2, Mail, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ITEM_TYPES = [
  { value: 'formation_frais_pedagogiques', label: 'Formation & frais pédagogiques' },
  { value: 'frais_annexes', label: 'Frais annexes' },
  { value: 'outils_pedagogiques', label: 'Outils pédagogiques' },
  { value: 'hors_formation', label: 'Hors formation' },
];

const TVA_TYPES = [
  { value: 'exempte', label: 'Exempté de TVA' },
  { value: 'tva_20', label: 'TVA 20%' },
  { value: 'tva_10', label: 'TVA 10%' },
  { value: 'tva_5_5', label: 'TVA 5,5%' },
];

const EMPTY_ITEM = {
  type: 'formation_frais_pedagogiques',
  name: 'Formation',
  details: '',
  prix_unitaire: 0,
  quantite: 1,
  tva: 0,
  type_tva: 'exempte',
  total_ht: 0,
  locked: false,
};

const today = new Date().toISOString().split('T')[0];
const prefix = `AFFEA-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

const InvoiceFormModal = ({ sessionId, client, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState({ mention: false, modalites: false, comptables: false });

  const [form, setForm] = useState({
    prefix: prefix,
    number: '',
    reference: '',
    date: today,
    bon_commande: '',
    dest_name: client?.name || '',
    dest_address: '',
    dest_code_postal: '',
    dest_ville: '',
    dest_pays: 'France',
    items: [{ ...EMPTY_ITEM }],
    mention_libre: '',
    modalites_reglement: 'Paiement à réception de facture',
    parametres_comptables: '',
  });

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM, name: '', type: 'formation_frais_pedagogiques' }] }));

  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      // Auto-compute total HT
      const pu = parseFloat(items[idx].prix_unitaire) || 0;
      const qty = parseFloat(items[idx].quantite) || 0;
      items[idx].total_ht = pu * qty;
      return { ...prev, items };
    });
  };

  const autoNumber = () => {
    updateField('number', String(Math.floor(Math.random() * 9000) + 1000));
  };

  // Compute totals
  const totalHT = form.items.reduce((s, item) => s + (parseFloat(item.total_ht) || 0), 0);
  const totalTVA = form.items.reduce((s, item) => {
    const ht = parseFloat(item.total_ht) || 0;
    const tva = parseFloat(item.tva) || 0;
    return s + (ht * tva / 100);
  }, 0);
  const totalTTC = totalHT + totalTVA;

  const handleSave = async () => {
    if (!form.dest_name.trim()) { toast.error('Le nom du destinataire est requis'); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: client?.client_id || '',
        client_name: client?.name || form.dest_name,
        prefix: form.prefix,
        number: form.number,
        reference: form.reference,
        date: form.date,
        bon_commande: form.bon_commande,
        destinataire: {
          name: form.dest_name,
          address: form.dest_address,
          code_postal: form.dest_code_postal,
          ville: form.dest_ville,
          pays: form.dest_pays,
        },
        items: form.items,
        mention_libre: form.mention_libre,
        modalites_reglement: form.modalites_reglement,
        parametres_comptables: form.parametres_comptables,
        total_ht: totalHT,
        total_tva: totalTVA,
        total_ttc: totalTTC,
      };
      const res = await fetch(`${API}/admin/sessions/${sessionId}/factures`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Facture créée avec succès');
        onSaved();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la création');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
    setSaving(false);
  };

  const formatPrice = (v) => parseFloat(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" data-testid="invoice-modal-overlay">
      <div className="min-h-screen flex items-start justify-center py-8 px-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-4xl shadow-2xl" data-testid="invoice-modal">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-slate-900 rounded-t-2xl border-b border-slate-200 dark:border-slate-700 p-5 z-10">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Créer une facture</h1>
            <div className="flex gap-2">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-invoice-btn">
                <Save className="h-4 w-4 mr-2" /> Enregistrer
              </Button>
              <Button variant="outline" onClick={onClose} data-testid="close-invoice-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Retour
              </Button>
            </div>
          </div>

          <div className="p-5 space-y-8">
            {/* ─── Informations générales ─── */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Informations générales</h2>
              <div className="flex items-center gap-3 mb-4 bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                <Building2 className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Client</p>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{client?.name || '—'}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Préfixe de la facture</label>
                  <Input value={form.prefix} onChange={(e) => updateField('prefix', e.target.value)} className="h-10" data-testid="input-invoice-prefix" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Numéro de la facture</label>
                  <Input value={form.number} onChange={(e) => updateField('number', e.target.value)} className="h-10" data-testid="input-invoice-number" />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={autoNumber} className="h-10 text-xs" data-testid="auto-number-btn">
                    <Plus className="h-3 w-3 mr-1" /> Affecter automatiquement
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Référence</label>
                  <Input value={form.reference} onChange={(e) => updateField('reference', e.target.value)} className="h-10" data-testid="input-invoice-ref" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Date</label>
                  <Input type="date" value={form.date} onChange={(e) => updateField('date', e.target.value)} className="h-10" data-testid="input-invoice-date" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Numéro de bon de commande</label>
                  <Input value={form.bon_commande} onChange={(e) => updateField('bon_commande', e.target.value)} className="h-10" data-testid="input-invoice-bon" />
                </div>
              </div>
            </div>

            {/* ─── Destinataire ─── */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Destinataire de la facture</h2>
              <div className="flex items-center gap-3 mb-4 bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                <Mail className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Destinataire</p>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{form.dest_name}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Nom</label>
                  <Input value={form.dest_name} onChange={(e) => updateField('dest_name', e.target.value)} className="h-10" data-testid="input-dest-name" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Adresse</label>
                  <Textarea value={form.dest_address} onChange={(e) => updateField('dest_address', e.target.value)} rows={2} data-testid="input-dest-address" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Code postal</label>
                    <Input value={form.dest_code_postal} onChange={(e) => updateField('dest_code_postal', e.target.value)} className="h-10" data-testid="input-dest-cp" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Ville</label>
                    <Input value={form.dest_ville} onChange={(e) => updateField('dest_ville', e.target.value)} className="h-10" data-testid="input-dest-ville" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Pays</label>
                    <Select value={form.dest_pays} onValueChange={(v) => updateField('dest_pays', v)}>
                      <SelectTrigger className="h-10" data-testid="select-dest-pays"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="France">France</SelectItem>
                        <SelectItem value="Belgique">Belgique</SelectItem>
                        <SelectItem value="Suisse">Suisse</SelectItem>
                        <SelectItem value="Canada">Canada</SelectItem>
                        <SelectItem value="Autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Items ─── */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Items</h2>
              <div className="space-y-4">
                {form.items.map((item, idx) => (
                  <div key={idx} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700" data-testid={`invoice-item-${idx}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500 mb-1 block">Type</label>
                        <Select value={item.type} onValueChange={(v) => updateItem(idx, 'type', v)}>
                          <SelectTrigger className="h-10 w-72" data-testid={`select-item-type-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ITEM_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <button onClick={() => removeItem(idx)} className="mt-6 p-2 text-slate-400 hover:text-red-500 rounded" data-testid={`delete-item-${idx}`}>
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Nom</label>
                        <Input value={item.name} onChange={(e) => updateItem(idx, 'name', e.target.value)} className="h-10" data-testid={`input-item-name-${idx}`} />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Détails</label>
                        <Textarea value={item.details} onChange={(e) => updateItem(idx, 'details', e.target.value)} rows={1} />
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-2 items-end">
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Prix unitaire</label>
                        <div className="relative">
                          <Input type="number" min={0} step={0.01} value={item.prix_unitaire} onChange={(e) => updateItem(idx, 'prix_unitaire', e.target.value)} className="h-10 pr-6" data-testid={`input-item-pu-${idx}`} />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Quantité</label>
                        <Input type="number" min={0} step={0.1} value={item.quantite} onChange={(e) => updateItem(idx, 'quantite', e.target.value)} className="h-10" data-testid={`input-item-qty-${idx}`} />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">TVA</label>
                        <div className="relative">
                          <Input type="number" min={0} step={0.1} value={item.tva} onChange={(e) => updateItem(idx, 'tva', e.target.value)} className="h-10 pr-6" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Type de TVA</label>
                        <Select value={item.type_tva} onValueChange={(v) => updateItem(idx, 'type_tva', v)}>
                          <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TVA_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Total HT</label>
                        <div className="relative">
                          <Input type="number" value={item.total_ht} readOnly className="h-10 pr-6 bg-slate-100 dark:bg-slate-700" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <Button variant="outline" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="h-4 w-4 mr-1" /> Ajouter
                </Button>
              </div>
            </div>

            {/* ─── Totals ─── */}
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-[10px] text-purple-600 dark:text-purple-400 uppercase">Total HT</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">{formatPrice(totalHT)} €</p>
                </div>
                <div>
                  <p className="text-[10px] text-purple-600 dark:text-purple-400 uppercase">TVA</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">{formatPrice(totalTVA)} €</p>
                </div>
                <div>
                  <p className="text-[10px] text-purple-600 dark:text-purple-400 uppercase">Total TTC</p>
                  <p className="text-xl font-bold text-purple-700 dark:text-purple-300">{formatPrice(totalTTC)} €</p>
                </div>
              </div>
            </div>

            {/* ─── Collapsible sections ─── */}
            {[
              { key: 'mention', label: 'Mention libre', field: 'mention_libre' },
              { key: 'modalites', label: 'Modalités de règlement', field: 'modalites_reglement' },
              { key: 'comptables', label: 'Paramètres comptables', field: 'parametres_comptables' },
            ].map(({ key, label, field }) => (
              <div key={key} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <button className="w-full flex items-center justify-between p-4" onClick={() => setSections(p => ({ ...p, [key]: !p[key] }))} data-testid={`toggle-${key}`}>
                  <span className="font-semibold text-sm text-slate-700 dark:text-slate-300">{label}</span>
                  {sections[key] ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {sections[key] && (
                  <div className="px-4 pb-4">
                    <Textarea value={form[field]} onChange={(e) => updateField(field, e.target.value)} rows={3} placeholder={`${label}...`} />
                  </div>
                )}
              </div>
            ))}

            {/* Bottom save */}
            <div className="flex gap-2 pt-2">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-invoice-bottom-btn">
                <Save className="h-4 w-4 mr-2" /> Enregistrer
              </Button>
              <Button variant="outline" onClick={onClose}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Retour
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceFormModal;
